import React from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import _CommonMultiSelect from '../../../Components/Utility/FilterAction/CommonMultiSelect';
import _CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import { ICustomAttributeMaster } from './customAttribute.types';

interface ICommonMultiSelectProps {
  label: React.ReactNode;
  required?: boolean;
  onChange: (selected: unknown, index?: number, label?: string) => void;
  selectedValue: unknown;
  optionList: unknown;
  selectLabel: string;
  selectValue: string;
  className?: string;
  disable?: boolean;
  index?: number;
  dataPlaceholder?: string;
  fromUser?: boolean;
  optionListData?: unknown;
}
const CommonMultiSelect = _CommonMultiSelect as unknown as React.FC<ICommonMultiSelectProps>;
const CustomTooltip = _CustomTooltip as unknown as React.FC<{ tooltipText: React.ReactNode; placement?: string; children?: React.ReactNode }>;

interface IPcType {
  _id: string;
  name: string;
  isActive?: boolean;
}
interface IBrand {
  _id: string;
  name: string;
  isActive?: boolean;
}
interface ISystemMastersDetails {
  pcTypeList: IPcType[];
  brandList: IBrand[];
}
interface IUsers {
  logInRights: unknown;
}
interface IRootState {
  Users: IUsers;
  SystemMastersDetails: ISystemMastersDetails;
}

interface IDatePickerCardProps {
  item: ICustomAttributeMaster;
  index: number;
  handleChanged: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
  activeInactiveClicked: (id: string) => void;
  handleDelete: (idOrIndex: string | number) => void;
  handleCheckboxCA: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDropSelectionChange: (e: unknown, index: number) => void;
  onDropBrandChange: (e: unknown, index: number) => void;
  isUniqueId?: number[] | undefined;
}

const DatePickerCard: React.FC<IDatePickerCardProps> = ({
  item,
  index,
  handleChanged,
  activeInactiveClicked,
  handleDelete,
  handleCheckboxCA,
  onDropSelectionChange,
  onDropBrandChange,
  isUniqueId,
}) => {
  const { t: _t } = useTranslation('common');
  const t = _t as unknown as TranslateFn; // TranslateFn declared globally in src/global.d.ts
  const { CheckActionPermission } = useComponent();

  const reducer = useSelector((s) => s as unknown as IRootState);
  const {logInRights} = reducer.Users;
  const pcTypeList = reducer.SystemMastersDetails.pcTypeList ?? [];
  const brandList = reducer.SystemMastersDetails.brandList ?? [];

  type CheckActionPermissionFn = (rights: unknown, section: string, module?: string, action?: string) => boolean;
  const checkPermission = CheckActionPermission as unknown as CheckActionPermissionFn;
  const masterAddEditAccess: boolean = checkPermission(logInRights, 'masters', 'customAttribute', 'addEdit');
  const masterDeleteAccess: boolean = checkPermission(logInRights, 'masters', 'customAttribute', 'delete');

  const productType: string[] = item.productType ?? [];
  const brand: string[] = item.brand ?? [];

  return (
    <div className="d-flex flex-wrap mt-2 align-items-center justify-content-center">
      {item._id !== '' && (
        <div className="checkbox-label ml-2">
          <input
            type="checkbox"
            name={`checkbox_DP${index}`}
            id={`customAttribute_DP_${item._id}`}
            onChange={handleCheckboxCA}
            checked={item.isChecked === true}
            disabled={!masterAddEditAccess}
          />
          <label htmlFor={`customAttribute_DP_${item._id}`} className="chklabel">
            <span />
          </label>
        </div>
      )}

      <div key={index} className="freetext-container mb-2 flex-fill">
        <div className="input-container">
          <div className="input-group form-floating-label fixed-label">
            <input
              key={index}
              name="freeLabel"
              autoFocus={!item._id}
              value={item.keyName ?? ''}
              placeholder={t('DATE_PICKER_CARD.ENTER_LABEL')}
              type="text"
              onChange={(e) => handleChanged(e, index)}
              className="form-control"
              disabled={!masterAddEditAccess}
            />
            <label className="text-bold f-16 asterisk-req">
              {t('DATE_PICKER_CARD.LABEL')}
              <span>*</span>
            </label>
          </div>
          {item.isEmpty && (item.keyName === null || item.keyName === '') && (
            <label className="text-danger m-0 p-1">{t('DATE_PICKER_CARD.LABEL_REQUIRED')}</label>
          )}
        </div>

        <div className="input-action d-flex">
          {item._id !== '' && masterAddEditAccess && (
            <CustomTooltip tooltipText={item.isActive ? 'Active' : 'Inactive'}>
              <button
                type="button"
                aria-label={item.isActive ? 'Active' : 'Inactive'}
                className="btn btn-outline mr-0"
                onClick={() => activeInactiveClicked(item._id)}
                id={item._id}
              >
                <span className={item.isActive ? 'icon-active f-24' : 'icon-inactive f-24'} />
              </button>
            </CustomTooltip>
          )}
          {masterDeleteAccess && (
            <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.DELETE')}>
              <button
                className="btn btn-outline mr-0"
                type="button"
                aria-label="Delete"
                onClick={() => {
                  handleDelete(item._id !== '' ? item._id : index);
                }}
              >
                <span className="icon-Delete f-24" />
              </button>
            </CustomTooltip>
          )}
        </div>
      </div>

      {!isEmpty(isUniqueId) && isUniqueId?.includes(index) === true && <label className="text-danger m-0 p-1">{t('DATE_PICKER_CARD.ENTRY_EXIST')}</label>}

      <div className="product-type-container type-text">
        <CommonMultiSelect
          label={t('DATE_PICKER_CARD.PRODUCT_TYPE')}
          required
          onChange={(e: unknown) => onDropSelectionChange(e, index)}
          selectedValue={productType}
          optionList={pcTypeList.map((e) => ({
            ...e,
            disabled: item._id !== '' && item.isUsedProductType?.includes(e._id?.toString()) === true && productType.includes(e._id?.toString()),
          }))}
          selectLabel="name"
          selectValue="_id"
          className="product-custom-multiselect common-new-multiselect w-100 text-bold mt-2"
          disable={!masterAddEditAccess}
        />
        {item.isEmpty && productType.length === 0 && <label className="text-danger m-0 p-1">{t('DATE_PICKER_CARD.PRODUCT_TYPE_REQUIRED')}</label>}
      </div>

      <div className="product-type-container type-text mt-2">
        <CommonMultiSelect
          label="Brand"
          required
          onChange={(e: unknown) => onDropBrandChange(e, index)}
          selectedValue={brand}
          optionList={brandList
            .filter((e) => e.isActive === true)
            .map((e) => ({
              ...e,
              disabled: item._id !== '' && item.isUsedBrand?.includes(e._id?.toString()) === true,
            }))}
          selectLabel="name"
          selectValue="_id"
          className="product-custom-multiselect common-new-multiselect w-100 text-bold mt-2"
          disable={!masterAddEditAccess}
        />
        {item.isEmpty && brand.length === 0 && <label className="text-danger m-0 p-1">{t('DATE_PICKER_CARD.BRAND_REQUIRED')}</label>}
      </div>
    </div>
  );
};

export default DatePickerCard;
